import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../baseComponents/Button'
import { TwinIconToolTip } from '../../../../../../../../../../baseComponents/TwinIcon'
import { OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import twinFetchPost from '../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../utils/globals/link'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { useLocation, useNavigate } from 'react-router'
import PermissionChecker from '../../../../../../../../../../baseComponents/PermissionChecker'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import useEditDeleteModal from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import { checkBuyDeliveryNoteAllOk } from '../../../../../../../../../../utils/globals/buy/buy'
import {BuyDownloadButtonCSV, BuyDownloadButtonPDF, BuyDownloadPdfTemplateButton, BuyDownloadTags, BuyPdfPreview, BuySupplierPdfPreview } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/BuyButtons'
import ModalDeleteBuyDeliveryNoteDraft from './ModalDeleteBuyDeliveryNoteDraft'
import { BuyDeliveryNoteDuplicateButton } from '../../../BuyDeliveryNoteButtons'
import { changeErrorMessage } from '../../../../../../../../../../utils/reducers/reduxDispatch'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'

const BuyDeliveryNoteCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageBuy } = useBuyDeliveryNoteCreateStepButtonsLogic({ data, onSubmit })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useBuyDeliveryNoteCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <BuyDeliveryNoteCreateButton  title={t('processBuyDeliveryNote', 'Procesar albarán')} disabled={infoMessageBuy ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageBuy || ''} />
        </div>
    )
}

const useBuyDeliveryNoteCreateStepButtonsLogic = ({ data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const Order = data?.header
    const name = 'buy_delivery_note_create_step_buttons'
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyBuy = orderEmployeeId === EmployeeId
    const hideDeleteButton = !isMyBuy || Order?.BuyOrderId
    let infoMessageBuy = checkBuyDeliveryNoteAllOk(data, t)

    const defaultButtons = [{ "id": "previewBuyDeliveryNotePDF" }, { "id": "downloadBuyDeliveryNote" }, { "id": "duplicateBuyDeliveryNote" }, { "id": "deleteBuyDeliveryNote" }]

    const buyDeliveryNoteDuplicateText = t('duplicateBuyDeliveryNote', 'Duplicar albarán de compra')
    const buyDeliveryNoteDeleteText = t('deleteDeliveryNoteDraft', 'Eliminar borrador albarán')
    const buyDeliveryNoteDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const buyDeliveryNoteDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const buyDeliveryNotePreviewPDFText = t('previewPdfTemplate', 'Previsualizar plantilla PDF')
    const buyDeliveryNoteDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const buyDeliveryNoteDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const buyDeliveryNoteDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const buyDeliveryNoteVariablesText = t('variables', 'Variables')
    const buyOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')
    const buyOrderPreviewSupplierPDFText = t('previewSupplierPDF', 'Previsualizar PDF proveedor')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateBuyDeliveryNote: {
                component: BuyDeliveryNoteDuplicateButton,
                extraProps: {
                    title: buyDeliveryNoteDuplicateText,
                    id: 'duplicateBuyDeliveryNote',
                    allRowData: data,
                    permissionTy: 'buy.deliveryNote',
                }
            },
            deleteBuyDeliveryNote: {
                component: BuyDeliveryNoteDeleteButton,
                showButton: !hideDeleteButton,
                extraProps: {
                    title: buyDeliveryNoteDeleteText,
                    id: 'deleteBuyDeliveryNote',
                    allRowData: data,
                }
            },
            downloadBuyCsv: {
                component: BuyDownloadButtonCSV,
                extraProps: {
                    title: buyDeliveryNoteDownloadCSVText,
                    id: 'downloadBuyCsv',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            downloadBuyTag: {
                component: BuyDownloadTags,
                extraProps: {
                    title: buyDeliveryNoteDownloadTagsText,
                    id: 'downloadBuyTag',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            previewBuySupplierPDF: {
                component: BuySupplierPdfPreview,
                showButton: Order.supplierPdf?.length,
                extraProps: {
                    title: buyOrderPreviewSupplierPDFText,
                    id: 'previewBuyOrderPDFSupplier',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'previewBuyOrderPDFSupplier'
                }
            },
            previewBuyDeliveryNotePDF: {
                component: BuyPdfPreview,
                extraProps: {
                    title: buyDeliveryNotePreviewPDFText,
                    id: 'previewBuyDeliveryNotePDF',
                    key: 'previewBuyDeliveryNotePDF',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            downloadBuyDeliveryNoteTemplate: {
                component: BuyDownloadPdfTemplateButton,
                extraProps: {
                    title: buyDeliveryNoteDownloadPDFTemplateText,
                    id: 'downloadBuyDeliveryNoteTemplate',
                    key: 'downloadBuyDeliveryNoteTemplate',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            downloadBuyDeliveryNote: {
                component: BuyDownloadButtonPDF,
                extraProps: {
                    title: buyDeliveryNoteDownloadPDFText,
                    id: 'downloadBuyDeliveryNote',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: buyDeliveryNoteDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: buyDeliveryNoteVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            buyProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: buyOrderProductTypesText,
                    id: 'buyProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData,
                }
            },
        }
    }, [Order, data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions, buyDeliveryNoteDeleteText, buyDeliveryNoteDivideIntoChaptersText, buyDeliveryNoteDownloadCSVText, buyDeliveryNoteDownloadPDFTemplateText, buyDeliveryNoteDownloadPDFText, buyDeliveryNoteDownloadTagsText, buyDeliveryNoteDuplicateText, buyDeliveryNotePreviewPDFText, buyDeliveryNoteVariablesText, hideDeleteButton, buyOrderProductTypesText, buyOrderPreviewSupplierPDFText])

    return { infoMessageBuy, name, ButtonComponents, defaultButtons }
}

export const BuyDeliveryNoteCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageBuy } = useBuyDeliveryNoteCreateStepButtonsLogic({ data, onSubmit })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useBuyDeliveryNoteCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <BuyDeliveryNoteCreateButton title={t('processBuyDeliveryNote', 'Procesar albarán')} disabled={infoMessageBuy ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageBuy || ''} />
        </div>
    )
}

type BuyDeliveryNoteCreateButtonProps = BuyDeliveryNoteCreateButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const BuyDeliveryNoteCreateButton: React.FC<BuyDeliveryNoteCreateButtonProps> = ({ title, OrderId, onSubmit, status, disabled, infoMessage, ...rest }) => {
    const { nextStepDeliveryNote, loading } = useBuyDeliveryNoteCreateButtonLogic({ OrderId, onSubmit, status })
    return (
        <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
            <div className='min-w-175'>
                <ButtonDisabledOrLoader onClick={nextStepDeliveryNote} buttonIsDisabled={disabled} textButton={title} loading={loading} className='w-full'/>
            </div>
        </ButtonOrderInfoLeftBase>
    )
}

interface BuyDeliveryNoteCreateButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
    status: number
}

const useBuyDeliveryNoteCreateButtonLogic = ({ onSubmit, OrderId, status }: BuyDeliveryNoteCreateButtonLogicProps) => {
    const {t} = useTwinTranslation()
    const errorUniqueNumberNLine = t('errorWithTheUniqueNumberNLine', 'Existe un error con el número de serie de la línea')
    const errorQuantityUniqueNumberNLine = t('errorQuantityWithTheUniqueNumberNLine', 'Existe un error con la cantidad del número de serie de la línea')
    const duplicateUniqueNumber = t('alreadyExistsUniqueNumber', 'Ya existe un producto con el número de serie')
    const noWarehouses = t('noWarehousesInWorkingCenter', 'Este centro de trabajo no tiene ningún almacén asociado')
    const { startLoading, endLoading, loading } = useIsLoading()

    const nextStepDeliveryNote = useCallback(async () => {
        startLoading?.()
        const response = await twinFetchPost('/api/app/order/buy/buyDeliveryNote/updateBuyDeliveryNote', { id: OrderId, status: status + 1 })
        if (response.status === 200) {
            onSubmit()
        } else {
            const responseJSON = await response.json()
            const error = responseJSON.errors[0].msg
            const splitError = error.split('-')
            if (splitError[0] === 'This serial number already exists') {
                changeErrorMessage(duplicateUniqueNumber + ' ' + splitError[1])
            } else if (splitError[0] === 'Serial Number is required') {
                changeErrorMessage(errorUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'This serial number has quantity problem'){
                changeErrorMessage(errorQuantityUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'noWarehouses') {
                changeErrorMessage(noWarehouses)
            }
        }
        endLoading?.()
    }, [OrderId, onSubmit, status, errorUniqueNumberNLine, errorQuantityUniqueNumberNLine, duplicateUniqueNumber, noWarehouses, startLoading, endLoading])

    return { nextStepDeliveryNote, loading }
}

interface BuyDeliveryNoteDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const BuyDeliveryNoteDeleteButton: React.FC<BuyDeliveryNoteDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useBuyDeliveryNoteDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('buy.deliveryNote')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteBuyDeliveryNoteDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
)
}

const useBuyDeliveryNoteDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'deliveryNotes')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

export default BuyDeliveryNoteCreateStepButtons